import React, { useEffect, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { History } from 'history'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import { Permission } from '../../core/auth0'

import { stationsActions } from '../../core/stations'
import { preMakingTemplatesActions } from '../../core/pre-making'
import { modalActions } from '../../core/modal'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { Station } from '../../core/stations'
import {
  PreMakingTemplate,
  CustomTemplateSchedule,
} from '../../core/pre-making'
import StationSelector from '../components/pre-making/station-selector'
import PreMakingTemplatesTable from '../components/pre-making/pre-making-templates-table'
import CustomTemplateScheduleTable from '../components/pre-making/custom-template-schedule-table'
import { preMakingContent } from '../components/pre-making/pre-making-content'

interface Props {
  stations: Station[]
  isLoading: boolean
  preMakingTemplates: PreMakingTemplate[]
  isTemplatesLoading: boolean
  customTemplateSchedules: CustomTemplateSchedule[]
  selectedStationId: number | null
  selectedStationName: string | null
  fetchStations: () => void
  fetchPreMakingTemplates: (stationId: number) => void
  makePreMakingTemplateActive: (templateId: number) => void
  makePreMakingTemplateInactive: (templateId: number) => void
  setSelectedStation: (
    stationId: number | null,
    stationName: string | null
  ) => void
  showCreatePreMakingTemplate: (stationId: number) => void
  history: History
}

interface SelectOption {
  value: string
  label: string
}

const PreMakingScreen: React.FC<Props> = ({
  stations,
  isLoading,
  preMakingTemplates,
  isTemplatesLoading,
  customTemplateSchedules,
  selectedStationId,
  selectedStationName,
  fetchStations,
  fetchPreMakingTemplates,
  makePreMakingTemplateActive,
  makePreMakingTemplateInactive,
  setSelectedStation,
  showCreatePreMakingTemplate,
  history,
}) => {
  const [
    localSelectedStation,
    setLocalSelectedStation,
  ] = useState<SelectOption | null>(null)

  useEffect(() => {
    fetchStations()
  }, [fetchStations])

  // Redux状態から選択されたステーションを復元
  useEffect(() => {
    if (selectedStationId && selectedStationName && stations.length > 0) {
      const station = stations.find(s => s.id === selectedStationId)
      if (station) {
        setLocalSelectedStation({
          value: station.id.toString(),
          label: station.name,
        })
        // テンプレートを再取得
        fetchPreMakingTemplates(selectedStationId)
      }
    }
  }, [
    selectedStationId,
    selectedStationName,
    stations,
    fetchPreMakingTemplates,
  ])

  const handleStationChange = (stationId: number | null) => {
    if (stationId) {
      const station = stations.find(s => s.id === stationId)
      if (station) {
        setLocalSelectedStation({
          value: station.id.toString(),
          label: station.name,
        })
        // Redux状態にも保存
        setSelectedStation(stationId, station.name)
        fetchPreMakingTemplates(stationId)
      }
    } else {
      setLocalSelectedStation(null)
      // Redux状態もクリア
      setSelectedStation(null, null)
    }
  }

  const handleTemplateDetail = (template: PreMakingTemplate) => {
    history.push(`/pre-making/templates/${template.id}`)
  }

  const handleCreateCustomTemplate = () => {
    if (selectedStationId) {
      showCreatePreMakingTemplate(selectedStationId)
    }
  }

  return (
    <Layout location={'pre_making'}>
      <Wrapper title={preMakingContent.title}>
        <StationSelector
          stations={stations}
          selectedStation={localSelectedStation}
          onStationChange={handleStationChange}
        />

        {localSelectedStation && (
          <div>
            <PreMakingTemplatesTable
              templates={preMakingTemplates}
              templateType="weekly"
              isLoading={isTemplatesLoading}
              onTemplateDetail={handleTemplateDetail}
              onMakeTemplateActive={makePreMakingTemplateActive}
              onMakeTemplateInactive={makePreMakingTemplateInactive}
            />

            <PreMakingTemplatesTable
              templates={preMakingTemplates}
              templateType="custom"
              isLoading={isTemplatesLoading}
              onTemplateDetail={handleTemplateDetail}
              onCreateCustomTemplate={handleCreateCustomTemplate}
            />

            <CustomTemplateScheduleTable
              schedules={customTemplateSchedules}
              isLoading={isTemplatesLoading}
            />
          </div>
        )}
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  stations: state.stations.stations,
  isLoading: state.stations.isLoading,
  preMakingTemplates: state.preMakingTemplates.preMakingTemplates,
  isTemplatesLoading: state.preMakingTemplates.isLoading,
  customTemplateSchedules: state.preMakingTemplates.customTemplateSchedules,
  selectedStationId: state.preMakingTemplates.selectedStationId,
  selectedStationName: state.preMakingTemplates.selectedStationName,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchStations: () => dispatch(stationsActions.fetchStations()),
  fetchPreMakingTemplates: (stationId: number) =>
    dispatch(preMakingTemplatesActions.fetchPreMakingTemplates(stationId)),
  makePreMakingTemplateActive: (templateId: number) =>
    dispatch(preMakingTemplatesActions.makePreMakingTemplateActive(templateId)),
  makePreMakingTemplateInactive: (templateId: number) =>
    dispatch(
      preMakingTemplatesActions.makePreMakingTemplateInactive(templateId)
    ),
  setSelectedStation: (stationId: number | null, stationName: string | null) =>
    dispatch(
      preMakingTemplatesActions.setSelectedStation(stationId, stationName)
    ),
  showCreatePreMakingTemplate: (stationId: number) =>
    dispatch(modalActions.showCreatePreMakingTemplate(stationId)),
})

const ConnectedComponent = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PreMakingScreen)
)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
